import { useNavigate, useParams } from "react-router-dom";
import { useState } from "react";
import { useDebounce } from "use-debounce";
import {
  Loader2,
  SquareArrowOutUpRight,
  Download,
  FileText,
  Search,
} from "lucide-react";

import Loading from "../components/common/LoaderCommon";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../utils/components/ui/tabs";
import CustomerReportConsolidatedReport from "./CustomerReportConsolidatedReport";
import PaginationComponent from "../components/PaginationComponent";
import { useDownloadAllInvoice } from "../service/customerService";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "../utils/components/ui/breadcrumb";
import { useGetAllInvoice } from "../service/invoiseService";

function CustomerHistory() {
  const navigate = useNavigate();
  const { id } = useParams();

  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize] = useState(10);
  const [searchKey, setSearchKey] = useState("");
  const [debouncedSearchKey] = useDebounce(searchKey, 500);

  const { mutate: downloadAllInvoices, isPending } = useDownloadAllInvoice();

  const { data, isLoading } = useGetAllInvoice({
    pageNo: currentPage,
    pageSize,
    customerId: id,
    invoiceNumber: debouncedSearchKey,
  });

  const tableData = data?.invoiceResponse || [];
  const totalElements = data?.totalElements || 0;
  const totalPages = Math.ceil(totalElements / pageSize);
  const customerData = tableData?.[0]?.customer;

  const handleDownload = () => {
    if (!id) return;
    downloadAllInvoices({
      customerId: Number(id),
      isDownload: true,
      docType: "PDF",
    });
  };

  if (isLoading) return <Loading />;

  return (
    <div className="space-y-6 p-6">
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink href="/">Home</BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbLink href="/customer">Customer</BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>Customer History</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      {/* Customer info card */}
      <div className="rounded-[26px] border border-[#E8EEF9] bg-white shadow-[0_16px_40px_rgba(26,94,249,0.08)] p-6">
        <div className="flex items-center gap-4 mb-5">
          <div className="w-12 h-12 rounded-full bg-[#EEF3FF] flex items-center justify-center flex-shrink-0">
            <span className="text-lg font-bold text-[#1A5EF9]">
              {(customerData?.username || "?").charAt(0).toUpperCase()}
            </span>
          </div>
          <div>
            <h2 className="text-lg font-semibold text-[#1A2B4A]">
              {customerData?.username || "—"}
            </h2>
            <p className="text-sm text-[#8A9BB5]">Customer Profile</p>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: "Phone", value: customerData?.phoneNumber },
            { label: "Email", value: customerData?.email },
            { label: "Address", value: customerData?.address },
          ].map(({ label, value }) => (
            <div key={label}>
              <p className="text-xs font-medium text-[#8A9BB5] mb-1">{label}</p>
              <p className="text-sm font-medium text-[#1A2B4A]">
                {value || "—"}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Tabs card */}
      <div className="rounded-[26px] border border-[#E8EEF9] bg-white shadow-[0_16px_40px_rgba(26,94,249,0.08)] p-6">
        <Tabs defaultValue="history">
          <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
            <TabsList className="h-11 bg-[#F0F4FF] rounded-[12px] p-1 gap-1">
              <TabsTrigger
                value="history"
                className="h-9 px-5 rounded-[10px] text-sm font-medium text-[#52627C] data-[state=active]:bg-[#1A5EF9] data-[state=active]:text-white data-[state=active]:shadow-[0_2px_8px_rgba(26,94,249,0.25)] transition-all"
              >
                Invoice History
              </TabsTrigger>
              <TabsTrigger
                value="report"
                className="h-9 px-5 rounded-[10px] text-sm font-medium text-[#52627C] data-[state=active]:bg-[#1A5EF9] data-[state=active]:text-white data-[state=active]:shadow-[0_2px_8px_rgba(26,94,249,0.25)] transition-all"
              >
                Consolidated Report
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="history">
            <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-4">
              <div className="relative w-full sm:w-[320px]">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#91A0B8]" />
                <input
                  type="text"
                  placeholder="Search by invoice number"
                  className="h-11 w-full rounded-[12px] border border-[#E5ECF8] bg-[#F5F8FD] pl-10 pr-4 text-sm text-[#23324D] outline-none transition focus:border-[#BFD1FF]"
                  value={searchKey}
                  onChange={(e) => setSearchKey(e.target.value)}
                />
              </div>
              <button
                onClick={handleDownload}
                disabled={isPending}
                className="h-11 rounded-[12px] bg-[#1A5EF9] px-5 text-white font-medium text-sm flex items-center gap-2 hover:bg-[#0E4FDE] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Download className="w-4 h-4" />
                )}
                Download All
              </button>
            </div>

            {tableData.length === 0 ? (
              <div className="py-16 text-center">
                <FileText className="w-10 h-10 text-[#D0DAEE] mx-auto mb-3" />
                <p className="text-sm text-[#52627C]">No invoices found</p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto rounded-[16px] border border-[#E8EEF9]">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-[#F5F8FD] text-left text-xs font-semibold text-[#31425E] uppercase tracking-wide">
                        <th className="px-5 py-3">#</th>
                        <th className="px-5 py-3">Invoice No.</th>
                        <th className="px-5 py-3">Purchase Date</th>
                        <th className="px-5 py-3 text-right">Total Amount</th>
                        <th className="px-5 py-3 text-right">Salesman Name</th>
                        <th className="px-5 py-3 text-right">
                          Amount Received
                        </th>
                        <th className="px-5 py-3">Status</th>
                        <th className="px-5 py-3 text-right">Balance</th>
                        <th className="px-5 py-3"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {tableData.map((item: any, idx: number) => (
                        <tr
                          key={item.id}
                          className="border-t border-[#EEF2FA] text-sm text-[#43536A] hover:bg-[#FAFCFF] transition-colors"
                        >
                          <td className="px-5 py-4 text-[#8A9BB5]">
                            {idx + 1 + currentPage * pageSize}
                          </td>
                          <td className="px-5 py-4 font-semibold text-[#24344F]">
                            {item?.invoiceNumber || "—"}
                          </td>
                          <td className="px-5 py-4">
                            {item?.salesDate ||
                              item?.saleDate ||
                              item?.quotationDate ||
                              "—"}
                          </td>
                          <td className="px-5 py-4 text-right font-medium text-[#1A2B4A]">
                            {Number(item.totalAmount || 0).toFixed(3)}
                          </td>
                          <td className="px-5 py-4 text-right">
                            {item?.staffName || "—"}
                          </td>
                          <td className="px-5 py-4 text-right">
                            OMR {Number(item?.paidAmount || 0).toFixed(3)}
                          </td>
                          <td className="px-5 py-4">
                            <span
                              className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${
                                item?.paymentStatus === "PAID"
                                  ? "bg-[#E6F9F0] text-[#1A9E5C]"
                                  : "bg-[#FFF7ED] text-[#E08A1E]"
                              }`}
                            >
                              {item?.paymentStatus || item?.status}
                            </span>
                          </td>
                          <td className="px-5 py-4 text-right">
                            {Number(item?.balanceAmount || 0).toFixed(3)}
                          </td>
                          <td className="px-5 py-4">
                            {item?.balanceAmount > 0 && (
                              <button
                                onClick={() =>
                                  navigate(
                                    `/customer-history/detail/${item.id}`,
                                  )
                                }
                                className="p-1.5 rounded-[8px] text-[#1A5EF9] hover:bg-[#EEF3FF] transition-colors"
                                title="Settle payment"
                                aria-label="Settle payment"
                              >
                                <SquareArrowOutUpRight className="w-4 h-4" />
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="flex justify-end mt-4">
                  <PaginationComponent
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                  />
                </div>
              </>
            )}
          </TabsContent>

          <TabsContent value="report">
            <CustomerReportConsolidatedReport customerId={id} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

export default CustomerHistory;
